import { zodResolver } from '@hookform/resolvers/zod';
import type { AnimationEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { selectSystemTenantConfig, selectSystemTenantConfigurationIsLoading } from '@/features/system/store/selectors';
import { inputsLength } from '@/domains/auth/constants/validation';
import AuthEvents from '@/domains/auth/events';
import { AuthPageContainer } from '@/domains/auth/layouts/AuthPageContainer/AuthPageContainer';
import { signIn } from '@/domains/auth/store/authSlice';
import { selectAuthSignInIsLoading } from '@/domains/auth/store/selectors';
import { FormInput } from '@/components/form/controlled/FormInput/FormInput';
import { LocalStorageVariables } from '@/enums/LocalStorageVariables';
import { useEventEffect } from '@/hooks/useEventEffect';
import { RootPaths } from '@/routes/paths';
import { useDispatch, useSelector } from '@/store/hooks';
import { AdaptiveButton, ForgotButton, ForgotOptions, Form, Options, SignInTitle, Title } from './SignIn.styles';
import { validationSchema } from './validationSchema';
import ClosedEyeIcon from '../../assets/icons/closedEye.svg?react';
import OpenedEyeIcon from '../../assets/icons/openedEye.svg?react';

const defaultValues = {
  username: '',
  password: '',
};

type FormValues = typeof defaultValues;

export const SignIn = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const isSignInLoading = useSelector(selectAuthSignInIsLoading);
  const tenantConfiguration = useSelector(selectSystemTenantConfig);
  const tenantConfigurationIsLoading = useSelector(selectSystemTenantConfigurationIsLoading);

  const [showPassword, setShowPassword] = useState(false);
  const [inputHasValue, setInputHasValue] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const handleShowPasswordClick = () => setShowPassword((show) => !show);

  // TODO: move this behaviour to all the input components
  const makeAnimationStartHandler = (stateSetter: (value: boolean) => void) => (e: AnimationEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    const autoFilled = !!target?.matches('*:-webkit-autofill');

    if (e.animationName === 'mui-auto-fill') {
      stateSetter(autoFilled);
    }

    if (e.animationName === 'mui-auto-fill-cancel') {
      stateSetter(autoFilled);
    }
  };

  const { username, password } = watch();

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    localStorage.setItem(LocalStorageVariables.UserName, data.username);
    dispatch(signIn(data));
  };

  const endAdornment = (
    <InputAdornment position="end">
      <IconButton
        data-testid="SigninTogglePasswordVisibility"
        aria-label="toggle password visibility"
        edge="end"
        onClick={handleShowPasswordClick}>
        {showPassword ? <ClosedEyeIcon /> : <OpenedEyeIcon />}
      </IconButton>
    </InputAdornment>
  );

  const loginWelcomeText = useMemo(() => {
    if (tenantConfiguration?.pages?.login?.loginWelcomeText) return tenantConfiguration.pages.login.loginWelcomeText;
    return tenantConfigurationIsLoading ? '' : 'Welcome back';
  }, [tenantConfigurationIsLoading, tenantConfiguration?.pages?.login?.loginWelcomeText]);

  const loginDescriptionText = useMemo(() => {
    if (tenantConfiguration?.pages?.login?.loginDescriptionText)
      return tenantConfiguration.pages.login.loginDescriptionText;
    return tenantConfigurationIsLoading ? '' : 'Welcome Back! Please Enter Your Details.';
  }, [tenantConfigurationIsLoading, tenantConfiguration?.pages?.login?.loginDescriptionText]);

  const loginButtonColor = useMemo(() => {
    if (tenantConfiguration?.pages?.login?.loginButtonColor) return tenantConfiguration.pages.login.loginButtonColor;
    return '';
  }, [tenantConfigurationIsLoading, tenantConfiguration?.pages?.login?.loginButtonColor]);

  useEffect(() => {
    clearErrors('username');
  }, [username, clearErrors]);

  useEffect(() => {
    clearErrors('password');
  }, [password, clearErrors]);

  useEventEffect(AuthEvents.RedirectToOtp, (username) => navigate(RootPaths.OTP, { state: { username } }));

  useEventEffect(AuthEvents.RedirectToUpdatePassword, () => navigate(RootPaths.UPDATE_PASSWORD));

  useEventEffect(AuthEvents.RedirectToDefault, () => navigate(RootPaths.ROOT));

  return (
    <AuthPageContainer>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSubmit(onSubmit)();
          }
        }}>
        <Title>
          <SignInTitle data-testid="SigninTitle" variant={isSmallScreen ? 'h5' : 'h3'}>
            {loginWelcomeText}
          </SignInTitle>
          <Typography variant={isSmallScreen ? 'body2' : 'body1'}>{loginDescriptionText}</Typography>
        </Title>
        <FormInput
          name="username"
          control={control}
          label="Username"
          errorMessage={errors.username?.message}
          InputLabelProps={{ shrink: inputHasValue || !!watch('username') }}
          inputProps={{
            maxLength: inputsLength.username.max,
            onAnimationStart: makeAnimationStartHandler(setInputHasValue),
            'data-testid': 'username',
          }}
        />
        <FormInput
          name="password"
          control={control}
          label="Password"
          InputProps={{ endAdornment }}
          type={showPassword ? 'text' : 'password'}
          errorMessage={errors.password?.message}
          InputLabelProps={{ shrink: inputHasValue || !!watch('password') }}
          inputProps={{
            maxLength: inputsLength.password.max,
            onAnimationStart: makeAnimationStartHandler(setInputHasValue),
          }}
        />
        <Options>
          <AdaptiveButton
            data-testid="SigninLoginButton"
            width="fluid"
            size={isSmallScreen ? 'small' : 'large'}
            type="submit"
            disabled={isSignInLoading}
            sx={loginButtonColor ? { backgroundColor: loginButtonColor } : {}}>
            LOG IN
          </AdaptiveButton>
          <ForgotOptions>
            <Link to={RootPaths.FORGOT_USERNAME}>
              <ForgotButton data-testid="SigninForgotUsernameButton" variant="additionalLinkSecondary" size="small">
                Forgot Username
              </ForgotButton>
            </Link>
            <Link to={RootPaths.FORGOT_PASSWORD}>
              <ForgotButton name="SigninForgotPasswordButton" variant="additionalLinkSecondary" size="small">
                Forgot Password
              </ForgotButton>
            </Link>
          </ForgotOptions>
        </Options>
      </Form>
    </AuthPageContainer>
  );
};
